import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Plug, Plus, Key, Copy, ArrowClockwise, Trash, Warning, Play, Code, ListDashes,
  CheckCircle, ProhibitInset,
} from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const INGEST_TYPES = [
  { v: "linkedhelper", l: "LinkedHelper" }, { v: "make", l: "Make.com" },
  { v: "n8n", l: "n8n" }, { v: "clay", l: "Clay" }, { v: "prospeo", l: "Prospeo" },
  { v: "internal_script", l: "Script interne" }, { v: "generic", l: "Générique" },
];

export default function AdminIntegrations() {
  const [integrations, setIntegrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newInt, setNewInt] = useState({ name: "", type: "linkedhelper", description: "" });
  const [revealKey, setRevealKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const [i, s] = await Promise.all([
        api.get("/admin/integrations"),
        api.get("/admin/integrations/stats"),
      ]);
      setIntegrations(i.data.items); setStats(s.data.today);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  const loadLogs = useCallback(async () => {
    try { const { data } = await api.get("/admin/integrations/logs?limit=100"); setLogs(data.items); } catch { /* */ }
  }, []);

  useEffect(() => { load(); loadLogs(); }, [load, loadLogs]);

  const createIntegration = async () => {
    if (!newInt.name.trim()) return toast.error("Nom requis");
    try {
      await api.post("/admin/integrations", newInt);
      toast.success("Intégration créée"); setShowNew(false); setNewInt({ name: "", type: "linkedhelper", description: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createKey = async (intId) => {
    try { const { data } = await api.post(`/admin/integrations/${intId}/keys`, { name: "Clé API" }); setRevealKey(data); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const revokeKey = async (keyId) => {
    try { await api.post(`/admin/integrations/keys/${keyId}/revoke`); toast.success("Clé révoquée"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const rotateKey = async (keyId) => {
    try { const { data } = await api.post(`/admin/integrations/keys/${keyId}/rotate`); setRevealKey(data); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-integrations-page" className="max-w-[1150px]">
      <div className="flex items-center gap-2 mb-1">
        <Plug size={22} weight="duotone" className="text-accent" />
        <h1 className="font-display text-3xl font-bold">Intégrations API</h1>
      </div>
      <p className="text-muted text-sm mb-6">API privée d'ingestion (ADMIN uniquement) pour injecter sociétés & contacts depuis LinkedHelper, Make, n8n, Clay… vers l'annuaire InterKO global. Non accessible aux clients ESN.</p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="ingest-stats">
          <Stat label="Appels (auj.)" value={stats.api_calls} />
          <Stat label="Sociétés créées" value={stats.companies_created} accent="#00E5FF" />
          <Stat label="Sociétés enrichies" value={stats.companies_updated} accent="#00E5FF" />
          <Stat label="Contacts créés" value={stats.contacts_created} accent="#22C55E" />
          <Stat label="Contacts enrichis" value={stats.contacts_updated} accent="#22C55E" />
          <Stat label="À vérifier" value={stats.review} accent="#F97316" />
          <Stat label="Échecs" value={stats.failed} accent="#EF4444" />
        </div>
      )}

      <Tabs defaultValue="integrations">
        <TabsList className="bg-surface-2 mb-4">
          <TabsTrigger value="integrations" data-testid="tab-integrations"><Plug size={14} className="mr-1.5" /> Intégrations</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs"><Code size={14} className="mr-1.5" /> Documentation</TabsTrigger>
          <TabsTrigger value="tester" data-testid="tab-tester"><Play size={14} className="mr-1.5" /> Testeur</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs" onClick={loadLogs}><ListDashes size={14} className="mr-1.5" /> Logs</TabsTrigger>
        </TabsList>

        {/* Integrations */}
        <TabsContent value="integrations">
          <button onClick={() => setShowNew(true)} data-testid="new-integration-btn"
            className="flex items-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift mb-4">
            <Plus size={16} weight="bold" /> Nouvelle intégration
          </button>
          <div className="space-y-4">
            {integrations.length === 0 && <div className="surface rounded-lg p-8 text-center text-muted">Aucune intégration. Créez-en une pour générer une clé API.</div>}
            {integrations.map((it) => (
              <div key={it.id} className="surface rounded-lg p-5" data-testid={`integration-${it.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold text-fg">{it.name}</span>
                      <span className="text-[10px] uppercase tracking-wide bg-surface-2 border border-border rounded px-1.5 py-0.5 text-muted">{it.type}</span>
                      {it.active ? <span className="text-[10px] text-[#22C55E]">● Actif</span> : <span className="text-[10px] text-muted">○ Inactif</span>}
                    </div>
                    <div className="text-xs text-muted mt-0.5">{it.record_count || 0} enregistrement(s){it.last_used_at ? ` · dernier appel ${new Date(it.last_used_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</div>
                  </div>
                  <button onClick={() => createKey(it.id)} data-testid={`create-key-${it.id}`}
                    className="flex items-center gap-1.5 text-sm border border-accent/40 text-accent px-3 py-1.5 rounded-md hover:bg-accent/10">
                    <Key size={14} /> Générer une clé
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(it.keys || []).map((k) => (
                    <div key={k.id} className="flex items-center gap-3 bg-surface-2 border border-border rounded-md px-3 py-2 text-sm" data-testid={`key-${k.id}`}>
                      <Key size={13} className={k.status === "active" ? "text-accent" : "text-muted"} />
                      <span className="font-mono text-muted">{k.prefix}…</span>
                      <span className="text-xs text-muted">{k.call_count || 0} appels</span>
                      {k.status === "active"
                        ? <span className="text-[10px] text-[#22C55E]">active</span>
                        : <span className="text-[10px] text-[#EF4444]">révoquée</span>}
                      <div className="ml-auto flex items-center gap-1">
                        {k.status === "active" && <>
                          <button onClick={() => rotateKey(k.id)} title="Rotation" className="text-muted hover:text-accent p-1" data-testid={`rotate-${k.id}`}><ArrowClockwise size={14} /></button>
                          <button onClick={() => revokeKey(k.id)} title="Révoquer" className="text-muted hover:text-[#EF4444] p-1" data-testid={`revoke-${k.id}`}><ProhibitInset size={14} /></button>
                        </>}
                      </div>
                    </div>
                  ))}
                  {(it.keys || []).length === 0 && <div className="text-xs text-muted">Aucune clé.</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="docs"><ApiDocs backend={BACKEND} /></TabsContent>
        <TabsContent value="tester"><ApiTester backend={BACKEND} /></TabsContent>
        <TabsContent value="logs"><LogsTable logs={logs} /></TabsContent>
      </Tabs>

      {/* New integration modal */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-surface border-border max-w-md" data-testid="new-integration-modal">
          <DialogTitle>Nouvelle intégration</DialogTitle>
          <DialogDescription className="text-muted text-xs">Identifie la source des données injectées.</DialogDescription>
          <div className="space-y-3 mt-2">
            <Input placeholder="Nom (ex : LinkedHelper Production)" value={newInt.name} onChange={(e) => setNewInt((s) => ({ ...s, name: e.target.value }))} className="bg-surface-2 border-border" data-testid="new-int-name" />
            <Select value={newInt.type} onValueChange={(v) => setNewInt((s) => ({ ...s, type: v }))}>
              <SelectTrigger className="bg-surface-2 border-border" data-testid="new-int-type"><SelectValue /></SelectTrigger>
              <SelectContent>{INGEST_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={createIntegration} className="w-full bg-accent text-black font-semibold px-4 py-2.5 rounded-md hover-lift" data-testid="new-int-submit">Créer</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reveal key modal */}
      <Dialog open={!!revealKey} onOpenChange={(v) => !v && setRevealKey(null)}>
        <DialogContent className="bg-surface border-border max-w-lg" data-testid="reveal-key-modal">
          <DialogTitle className="flex items-center gap-2"><Key size={16} className="text-accent" /> Votre clé API</DialogTitle>
          <DialogDescription className="text-[#F5A623] text-xs flex items-center gap-1.5"><Warning size={13} /> {revealKey?.warning}</DialogDescription>
          <div className="flex items-center gap-2 bg-surface-2 border border-border rounded-md p-3 mt-2">
            <code className="text-xs text-accent break-all flex-1" data-testid="revealed-key">{revealKey?.key}</code>
            <button onClick={() => { navigator.clipboard.writeText(revealKey?.key || ""); toast.success("Copié"); }} className="text-muted hover:text-fg p-1" data-testid="copy-key-btn"><Copy size={16} /></button>
          </div>
          <button onClick={() => setRevealKey(null)} className="mt-3 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift">J'ai copié la clé</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="surface rounded-lg p-3">
      <div className="text-xl font-display font-bold" style={{ color: accent || "#F4F4F5" }}>{value ?? 0}</div>
      <div className="text-[11px] text-muted mt-0.5">{label}</div>
    </div>
  );
}

function ApiDocs({ backend }) {
  const base = `${backend}/api/admin/v1/ingest`;
  const example = `curl -X POST ${base}/company-contact \\
  -H "Authorization: Bearer ik_admin_live_xxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source": "linkedhelper",
    "external_id": "LH-RECORD-998312",
    "company": { "name": "Leroy Merlin", "domain": "leroymerlin.fr" },
    "contact": {
      "first_name": "Jean", "last_name": "Martin",
      "job_title": "Head of Talent Acquisition",
      "email": "jean.martin@example.com",
      "linkedin_url": "https://www.linkedin.com/in/example"
    }
  }'`;
  return (
    <div className="surface rounded-lg p-6 space-y-4 text-sm" data-testid="api-docs">
      <div>
        <div className="overline mb-1">Base URL</div>
        <code className="text-accent text-xs">{base}</code>
      </div>
      <div>
        <div className="overline mb-1">Authentification</div>
        <p className="text-muted text-xs">En-tête <code className="text-fg">Authorization: Bearer &lt;clé&gt;</code>. Les clés commencent par <code className="text-fg">ik_admin_live_</code>. Une session client ESN ne peut PAS appeler ces endpoints (401/403).</p>
      </div>
      <div>
        <div className="overline mb-1">Endpoints</div>
        <ul className="text-xs text-muted space-y-1 font-mono">
          <li><span className="text-[#22C55E]">POST</span> /company — sociétés</li>
          <li><span className="text-[#22C55E]">POST</span> /contact — contacts</li>
          <li><span className="text-[#22C55E]">POST</span> /company-contact — société + contact (recommandé)</li>
          <li><span className="text-[#22C55E]">POST</span> /batch — lot (max 2000, champ <code>records</code>)</li>
        </ul>
      </div>
      <div>
        <div className="overline mb-1">Idempotence & déduplication</div>
        <p className="text-muted text-xs">Fournissez <code className="text-fg">external_id</code> stable par enregistrement. Les contacts sont dédupliqués sur email → LinkedIn → external_id → nom + société. Les sociétés via ID interKO → domaine → LinkedIn → nom+ville. Enrichissement progressif : un renvoi enrichit l'existant sans doublon.</p>
      </div>
      <div>
        <div className="overline mb-1">Exemple</div>
        <pre className="bg-surface-2 border border-border rounded-md p-3 text-[11px] text-fg overflow-x-auto whitespace-pre-wrap">{example}</pre>
      </div>
    </div>
  );
}

function ApiTester({ backend }) {
  const [endpoint, setEndpoint] = useState("company-contact");
  const [key, setKey] = useState("");
  const [payload, setPayload] = useState('{\n  "source": "linkedhelper",\n  "company": { "name": "Example Corp", "domain": "example.com" },\n  "contact": { "first_name": "Marie", "last_name": "Dupont", "job_title": "Talent Acquisition Manager", "email": "marie@example.com" }\n}');
  const [resp, setResp] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true); setResp(null);
    try {
      let body;
      try { body = JSON.parse(payload); } catch { setResp({ error: "JSON invalide" }); setBusy(false); return; }
      const r = await fetch(`${backend}/api/admin/v1/ingest/${endpoint}`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${key.trim()}` },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      setResp({ status: r.status, data });
    } catch (e) { setResp({ error: String(e) }); } finally { setBusy(false); }
  };

  return (
    <div className="surface rounded-lg p-6 space-y-3" data-testid="api-tester">
      <div className="flex gap-2">
        <Select value={endpoint} onValueChange={setEndpoint}>
          <SelectTrigger className="w-52 bg-surface-2 border-border" data-testid="tester-endpoint"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="company">/company</SelectItem>
            <SelectItem value="contact">/contact</SelectItem>
            <SelectItem value="company-contact">/company-contact</SelectItem>
            <SelectItem value="batch">/batch</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Clé API (ik_admin_live_…)" value={key} onChange={(e) => setKey(e.target.value)} className="flex-1 bg-surface-2 border-border font-mono text-xs" data-testid="tester-key" />
      </div>
      <textarea value={payload} onChange={(e) => setPayload(e.target.value)} rows={9} className="w-full bg-surface-2 border border-border rounded-md p-3 text-xs text-fg font-mono outline-none focus:border-accent/40" data-testid="tester-payload" />
      <button onClick={send} disabled={busy || !key.trim()} className="flex items-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50" data-testid="tester-send">
        <Play size={15} weight="fill" /> {busy ? "Envoi…" : "Envoyer"}
      </button>
      {resp && (
        <div className="mt-2" data-testid="tester-response">
          <div className="overline mb-1 flex items-center gap-1.5">
            {resp.status && (resp.status < 300 ? <CheckCircle size={13} className="text-[#22C55E]" /> : <Warning size={13} className="text-[#EF4444]" />)}
            Réponse {resp.status ? `· HTTP ${resp.status}` : ""}
          </div>
          <pre className="bg-surface-2 border border-border rounded-md p-3 text-[11px] text-fg overflow-x-auto whitespace-pre-wrap">{JSON.stringify(resp.data ?? resp.error, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function LogsTable({ logs }) {
  return (
    <div className="surface rounded-lg p-5" data-testid="logs-table">
      {logs.length === 0 ? <div className="text-center text-muted py-8">Aucun appel enregistré.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-muted border-b border-border">
              <th className="text-left py-2 font-medium">Date</th><th className="text-left font-medium">Source</th>
              <th className="text-left font-medium">Endpoint</th><th className="text-right font-medium">Traités</th>
              <th className="text-right font-medium">Créés</th><th className="text-right font-medium">MàJ</th>
              <th className="text-right font-medium">À vérif.</th><th className="text-right font-medium">Échecs</th></tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-border/40">
                  <td className="py-2 font-mono text-muted">{l.at ? new Date(l.at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="text-fg">{l.source}</td><td className="text-muted">{l.endpoint}</td>
                  <td className="text-right">{l.processed}</td><td className="text-right text-[#22C55E]">{l.created}</td>
                  <td className="text-right">{l.updated}</td><td className="text-right text-[#F97316]">{l.review}</td>
                  <td className="text-right text-[#EF4444]">{l.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
